(() => {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const mobileMq = window.matchMedia("(max-width: 1000px)");

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const getStickyOffset = () => {
    if (window.si26Sticky?.getScrollOffset) {
      return window.si26Sticky.getScrollOffset();
    }
    const nav = document.querySelector("[data-si26-nav]");
    const header = document.querySelector(".site-header");
    const chrome = header ? Math.round(header.getBoundingClientRect().top + header.offsetHeight) : 0;
    if (nav) return Math.max(0, chrome + Math.round(nav.offsetHeight || 0));
    return Math.max(0, chrome);
  };

  const initBrand = (root) => {
    if (!root || root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const sticky = root.querySelector(".si26-brand__sticky");
    const frame = root.querySelector("[data-si26-brand-frame]");
    const videos = [...root.querySelectorAll(".si26-brand__video, video")];
    let maxProgress = 0;
    let played = false;
    let exitPaused = false;
    let ticking = false;
    let stickyOffsetApplied = -1;
    let mobileEndW = 0;
    let mobileEndH = 0;

    videos.forEach((video) => {
      if (!video.classList.contains("si26-brand__video--pc")) return;
      video.loop = false;
      video.addEventListener("ended", () => {
        video.pause();
      });
    });

    const activeVideos = () => {
      if (!videos.length) return [];
      const preferMobile = mobileMq.matches;
      const matched = videos.filter((video) => {
        const isMobile = video.classList.contains("si26-brand__video--mobile");
        const isPc = video.classList.contains("si26-brand__video--pc");
        if (preferMobile) return isMobile || (!isMobile && !isPc);
        return isPc || (!isMobile && !isPc);
      });
      return matched.length ? matched : videos;
    };

    const playVideo = () => {
      if (played || exitPaused) return;
      played = true;
      activeVideos().forEach((video) => {
        if (video.classList.contains("si26-brand__video--pc")) video.loop = false;
        if (video.ended) return;
        const playPromise = video.play?.();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise.then(() => {
            if (exitPaused) video.pause();
          }).catch(() => {});
        }
      });
    };

    const pauseVideo = () => {
      exitPaused = true;
      activeVideos().forEach((video) => {
        if (!video.ended) video.pause?.();
      });
    };

    const resumeVideo = () => {
      const pending = activeVideos().filter((video) => !video.ended && video.paused);
      if (!pending.length) return;
      exitPaused = false;
      pending.forEach((video) => {
        const playPromise = video.play?.();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise.then(() => {
            if (exitPaused) video.pause();
          }).catch(() => {});
        }
      });
    };

    const complete = () => {
      root.classList.add("si26-is-complete");
      if (!reduceMotion.matches) playVideo();
    };

    const applyMobileFrame = (p) => {
      if (!frame || !sticky) return;
      if (!mobileEndW || !mobileEndH) {
        mobileEndW = Math.max(1, sticky.clientWidth);
        mobileEndH = Math.max(1, sticky.clientHeight);
      }
      const start = 361;
      const clamped = clamp(p, 0, 1);
      const sx = (start + (mobileEndW - start) * clamped) / mobileEndW;
      const sy = Math.max(0.01, (start + (mobileEndH - start) * clamped) / mobileEndH);
      frame.style.setProperty("--si26-brand-sx", sx.toFixed(4));
      frame.style.setProperty("--si26-brand-sy", sy.toFixed(4));
      frame.style.setProperty("--si26-brand-shift", `${(60 * (1 - clamped)).toFixed(2)}px`);
    };

    const update = () => {
      const offset = getStickyOffset();
      if (sticky && stickyOffsetApplied !== offset) {
        stickyOffsetApplied = offset;
        mobileEndW = 0;
        mobileEndH = 0;
        sticky.style.top = `${offset}px`;
        sticky.style.height = `calc(100vh - ${offset}px)`;
        sticky.style.setProperty("--si26-sticky-offset", `${offset}px`);
        root.style.setProperty("--si26-sticky-offset", `${offset}px`);
        document.documentElement.style.setProperty("--si26-sticky-offset", `${offset}px`);
      }

      if (reduceMotion.matches) {
        maxProgress = 1;
        if (frame) frame.style.setProperty("--si26-brand-progress", "1");
        complete();
        return;
      }

      const sectionTop = root.getBoundingClientRect().top;
      let progress = maxProgress;

      if (mobileMq.matches) {
        // iOS won't paint a video layer until playback has started, and it
        // won't start mid-gesture. Prime it as the section approaches.
        if (sectionTop < window.innerHeight * 1.15) playVideo();

        if (maxProgress >= 1) {
          applyMobileFrame(1);
          if (frame) frame.style.setProperty("--si26-brand-progress", "1");
          document.documentElement.style.setProperty("--si26-brand-exit", "0");
          complete();
          return;
        }

        // Visual card is scaled, so don't measure the layout box.
        // Resting midpoint is the 60px gap plus half of the 361px card.
        const anchor = 60 + 361 / 2;
        const expandDistance = Math.max(1, Math.round(window.innerHeight * 0.3 + offset));
        const traveled = window.innerHeight - anchor - sectionTop;
        progress = Math.max(maxProgress, clamp(traveled / expandDistance, 0, 1));
        applyMobileFrame(progress);
      } else if (maxProgress < 1) {
        // Same distance as the previous 130vh track, so the expand pace stays put.
        const expandDistance = Math.max(1, Math.round(window.innerHeight * 0.3 + offset));
        const scrolled = clamp(offset - sectionTop, 0, expandDistance);
        progress = Math.max(maxProgress, clamp(scrolled / expandDistance, 0, 1));
      }

      maxProgress = progress;

      // Exit is the scroll left in the pin after the expand.
      // The products section stays below the card, so it meets the bottom edge
      // instead of sliding under the video.
      let exit = 0;
      if (!mobileMq.matches && maxProgress >= 1) {
        const expandDistance = Math.max(1, Math.round(window.innerHeight * 0.3 + offset));
        const stickyHeight = Math.max(1, window.innerHeight - offset);
        const pinDistance = Math.max(expandDistance + 1, root.offsetHeight - stickyHeight);
        const scrolled = Math.max(0, offset - sectionTop);
        exit = clamp((scrolled - expandDistance) / (pinDistance - expandDistance), 0, 1);
      }

      const exitValue = mobileMq.matches ? "0" : String(exit);
      if (frame) {
        frame.style.setProperty("--si26-brand-progress", String(maxProgress >= 1 ? 1 : progress));
        frame.style.setProperty("--si26-brand-exit", exitValue);
      }
      document.documentElement.style.setProperty("--si26-brand-exit", exitValue);

      if (progress >= 0.98) {
        maxProgress = 1;
        if (mobileMq.matches) applyMobileFrame(1);
        if (frame) frame.style.setProperty("--si26-brand-progress", "1");
        complete();
      }

      if (!mobileMq.matches && maxProgress >= 1) {
        if (exit >= 0.98) {
          if (frame) frame.style.setProperty("--si26-brand-exit", "1");
          document.documentElement.style.setProperty("--si26-brand-exit", "1");
          pauseVideo();
        } else if (exitPaused) {
          resumeVideo();
        }
      }
    };

    const onScrollOrResize = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", () => {
      mobileEndW = 0;
      mobileEndH = 0;
      onScrollOrResize();
    });
    update();
  };

  const init = (scope = document) => {
    if (scope.matches?.("[data-si26-brand]")) {
      initBrand(scope);
    }
    scope.querySelectorAll?.("[data-si26-brand]").forEach(initBrand);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
